import { isAxiosError } from 'axios'

import api from './api'
import type {
  BindingIssue,
  DeviceBinding,
  DeviceUpgradeArtifact,
  DeviceUpgradeArtifactMetadata,
  DeviceUpgradeBatch,
  DeviceUpgradeBatchConflictDetail,
  DeviceUpgradeBatchDevicePage,
  SelectableUpgradeDevicePage,
} from '../types/device-upgrade'
import { normalizeSearchText } from '../utils/searchText'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

export const extractDeviceUpgradeBatchConflict = (
  error: unknown,
): DeviceUpgradeBatchConflictDetail | null => {
  if (!isAxiosError(error)) return null
  const detail = error.response?.data?.detail
  if (!isRecord(detail) || detail.code !== 'DEVICE_UPGRADE_BATCH_CONFLICT') return null
  if (
    typeof detail.message !== 'string' ||
    typeof detail.selected_count !== 'number' ||
    typeof detail.conflict_count !== 'number' ||
    !Array.isArray(detail.conflicts)
  ) {
    return null
  }

  const conflicts = detail.conflicts.flatMap((value) => {
    if (!isRecord(value) || typeof value.device_id !== 'string' || !Array.isArray(value.running_batches)) {
      return []
    }
    const runningBatches = value.running_batches.flatMap((batch) => {
      if (!isRecord(batch) || typeof batch.batch_id !== 'number') return []
      return [{
        batch_id: batch.batch_id,
        batch_name: typeof batch.batch_name === 'string' ? batch.batch_name : null,
      }]
    })
    return [{
      device_id: value.device_id,
      device_name: typeof value.device_name === 'string' ? value.device_name : null,
      running_batches: runningBatches,
    }]
  })

  return {
    code: 'DEVICE_UPGRADE_BATCH_CONFLICT',
    message: detail.message,
    selected_count: detail.selected_count,
    conflict_count: detail.conflict_count,
    conflicts,
  }
}

export const getDeviceUpgradeArtifacts = async (): Promise<DeviceUpgradeArtifact[]> => {
  const response = await api.get('/operations/device-upgrades/artifacts')
  return response.data
}

export const inspectDeviceUpgradeArtifact = async (
  artifactFile: File,
): Promise<DeviceUpgradeArtifactMetadata> => {
  const formData = new FormData()
  formData.append('artifact_file', artifactFile)
  const response = await api.post('/operations/device-upgrades/artifacts/inspect', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export const registerPrebuiltDeviceUpgradeArtifact = async (payload: {
  name?: string
  package_type?: string
  version?: string
  device_type?: string
  artifact_name?: string
  description?: string
  is_stable: boolean
  artifact_file: File
}): Promise<DeviceUpgradeArtifact> => {
  const formData = new FormData()
  formData.append('name', payload.name || '')
  formData.append('package_type', payload.package_type || '')
  formData.append('version', payload.version || '')
  formData.append('device_type', payload.device_type || '')
  formData.append('artifact_name', payload.artifact_name || '')
  formData.append('description', payload.description || '')
  formData.append('is_stable', String(payload.is_stable))
  formData.append('artifact_file', payload.artifact_file)
  const response = await api.post('/operations/device-upgrades/artifacts/prebuilt', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export const uploadArtifactToMender = async (
  artifactId: number,
): Promise<DeviceUpgradeArtifact> => {
  const response = await api.post(`/operations/device-upgrades/artifacts/${artifactId}/upload-mender`)
  return response.data
}

export const disableDeviceUpgradeArtifact = async (
  artifactId: number,
  payload: { description: string },
): Promise<DeviceUpgradeArtifact> => {
  const response = await api.put(`/operations/device-upgrades/artifacts/${artifactId}/disable`, payload)
  return response.data
}

export const updateDeviceUpgradeArtifactDescription = async (
  artifactId: number,
  payload: { description?: string | null },
): Promise<DeviceUpgradeArtifact> => {
  const response = await api.patch(`/operations/device-upgrades/artifacts/${artifactId}`, payload)
  return response.data
}

export const enableDeviceUpgradeArtifact = async (
  artifactId: number,
): Promise<DeviceUpgradeArtifact> => {
  const response = await api.put(`/operations/device-upgrades/artifacts/${artifactId}/enable`)
  return response.data
}

export const deleteDeviceUpgradeArtifact = async (
  artifactId: number,
): Promise<void> => {
  await api.delete(`/operations/device-upgrades/artifacts/${artifactId}`)
}

type SyncMenderDevicesOptions = {
  operationSource?: 'manual'
}

export const syncMenderDevices = async (options?: SyncMenderDevicesOptions): Promise<{
  bound: number
  updated: number
  issues: number
  skipped?: boolean
  reason?: string
  retry_after_seconds?: number
}> => {
  const response = options?.operationSource === 'manual'
    ? await api.post(
      '/operations/device-upgrades/mender-devices/sync',
      undefined,
      { params: { operation_source: 'manual' } },
    )
    : await api.post('/operations/device-upgrades/mender-devices/sync')
  return response.data
}

export const checkMenderDeviceStatus = async (
  deviceId: string,
): Promise<DeviceBinding> => {
  const response = await api.post(
    `/operations/device-upgrades/device-bindings/${encodeURIComponent(deviceId)}/mender-status-check`,
  )
  return response.data
}

export const getDeviceBindings = async (params?: {
  keyword?: string
  project_id?: string | number
  device_status?: string
  binding_status?: string
}): Promise<DeviceBinding[]> => {
  const response = await api.get('/operations/device-upgrades/device-bindings', {
    params: {
      keyword: normalizeSearchText(params?.keyword) || undefined,
      project_id: params?.project_id && params.project_id !== 'all' ? params.project_id : undefined,
      device_status: params?.device_status && params.device_status !== 'all' ? params.device_status : undefined,
      binding_status: params?.binding_status && params.binding_status !== 'all' ? params.binding_status : undefined,
    },
  })
  return response.data
}

const joinNumberCsv = (values?: number[]): string | undefined =>
  values && values.length > 0 ? values.join(',') : undefined

export const getSelectableUpgradeDevices = async (params: {
  artifact_id: number
  page: number
  page_size: number
  keyword?: string
  project_ids?: number[]
  region_ids?: number[]
  binding_status?: string
  device_status?: string
  mender_reachability?: string
}): Promise<SelectableUpgradeDevicePage> => {
  const response = await api.get('/operations/device-upgrades/device-bindings/selectable', {
    params: {
      artifact_id: params.artifact_id,
      page: params.page,
      page_size: params.page_size,
      keyword: normalizeSearchText(params.keyword) || undefined,
      project_ids: joinNumberCsv(params.project_ids),
      region_ids: joinNumberCsv(params.region_ids),
      binding_status: params.binding_status || undefined,
      device_status: params.device_status || undefined,
      mender_reachability: params.mender_reachability || undefined,
    },
  })
  return response.data
}

export const getBindingIssues = async (): Promise<BindingIssue[]> => {
  const response = await api.get('/operations/device-upgrades/device-bindings/issues')
  return response.data
}

export const bindMenderDevice = async (
  deviceId: string,
  payload: { mender_device_id: string; force?: boolean },
): Promise<DeviceBinding> => {
  const response = await api.post(`/operations/device-upgrades/device-bindings/${deviceId}/bind`, payload)
  return response.data
}

export const unbindMenderDevice = async (deviceId: string): Promise<DeviceBinding> => {
  const response = await api.post(`/operations/device-upgrades/device-bindings/${deviceId}/unbind`)
  return response.data
}

export const getDeviceUpgradeBatches = async (): Promise<DeviceUpgradeBatch[]> => {
  const response = await api.get('/operations/device-upgrades/batches')
  return response.data
}

export const getDeviceUpgradeBatch = async (
  batchId: number,
): Promise<DeviceUpgradeBatch> => {
  const response = await api.get(`/operations/device-upgrades/batches/${batchId}`)
  return response.data
}

export const getDeviceUpgradeBatchDevices = async (
  batchId: number,
  params: {
    page: number
    page_size: number
    keyword?: string
    status?: string
  },
): Promise<DeviceUpgradeBatchDevicePage> => {
  const response = await api.get(`/operations/device-upgrades/batches/${batchId}/devices`, {
    params: {
      page: params.page,
      page_size: params.page_size,
      keyword: normalizeSearchText(params.keyword) || undefined,
      status: params.status || undefined,
    },
  })
  return response.data
}

export const createDeviceUpgradeBatch = async (payload: {
  name: string
  description?: string
  artifact_id: number
  device_ids: string[]
}): Promise<DeviceUpgradeBatch> => {
  const response = await api.post('/operations/device-upgrades/batches', payload)
  return response.data
}

export const syncDeviceUpgradeBatch = async (
  batchId: number,
): Promise<DeviceUpgradeBatch> => {
  const response = await api.post(
    `/operations/device-upgrades/batches/${batchId}/sync`,
    undefined,
    { params: { operation_source: 'manual' } },
  )
  return response.data
}

export const cancelDeviceUpgradeBatch = async (
  batchId: number,
): Promise<DeviceUpgradeBatch> => {
  const response = await api.post(`/operations/device-upgrades/batches/${batchId}/cancel`)
  return response.data
}

export const retryFailedDeviceUpgradeBatch = async (
  batchId: number,
): Promise<DeviceUpgradeBatch> => {
  const response = await api.post(`/operations/device-upgrades/batches/${batchId}/retry-failed`)
  return response.data
}

export const rollbackDeviceUpgradeBatch = async (
  batchId: number,
): Promise<DeviceUpgradeBatch> => {
  const response = await api.post(`/operations/device-upgrades/batches/${batchId}/rollback`)
  return response.data
}
